import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, RefreshCw, Search, Users, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getPmCalendar, getPmGanttMembers, getPmCrmProjects, getPmCalendarStatuses } from '../ProjectManagement/api/projectManagementApi';
import './PmCalendar.css';

const pad = (value) => String(value).padStart(2, '0');
const toDateValue = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
const parseDate = (value) => value ? new Date(`${String(value).slice(0, 10)}T00:00:00`) : null;
const monthStart = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const monthEnd = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const DEFAULT_SUBTITLE = 'See when team members are already allocated or blocked by PM tasks.';
const PmCalendarPanel = ({
  embedded = false,
  title = 'Team Calendar',
  subtitle = DEFAULT_SUBTITLE,
}) => {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => monthStart(new Date()));
  const [startDate, setStartDate] = useState(() => toDateValue(monthStart(new Date())));
  const [endDate, setEndDate] = useState(() => toDateValue(monthEnd(new Date())));
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [selectedStatusKeys, setSelectedStatusKeys] = useState([]);
  const [openFilter, setOpenFilter] = useState('');
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);

  const load = async ({ syncCursorToRange = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      if (syncCursorToRange) {
        const rangeStart = parseDate(startDate);
        if (rangeStart) setCursor(monthStart(rangeStart));
      }
      const [calendar, projectRows, memberRows, statusRows] = await Promise.all([
        getPmCalendar({
          start_date: startDate,
          end_date: endDate,
        }),
        getPmCrmProjects(),
        getPmGanttMembers(),
        getPmCalendarStatuses(),
      ]);
      setEvents(calendar?.events || []);
      setProjects(projectRows || []);
      setMembers(memberRows || []);
      setStatuses(statusRows || []);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to load Project Management calendar.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const calendarDays = useMemo(() => {
    const first = monthStart(cursor);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, index) => {
      const day = new Date(start);
      day.setDate(start.getDate() + index);
      return day;
    });
  }, [cursor]);

  const nameFor = (id) => members.find((member) => String(member.user_id || member.id) === String(id))?.name || `User #${id}`;
  const eventMembers = (event) => Array.from(new Set(
    [event.assigned_to, ...String(event.collaborators || '').split(',')]
      .map((id) => String(id || '').trim())
      .filter(Boolean)
  ));
  const filteredEvents = useMemo(() => (
    events.filter((event) => matchesSelectedFilters(event, {
      selectedProjectIds,
      selectedMemberIds,
      selectedStatusKeys,
      assigneeIds: eventMembers(event),
      assigneeNames: eventMembers(event).map(nameFor),
      searchTerm,
    }))
  ), [events, selectedProjectIds, selectedMemberIds, selectedStatusKeys, searchTerm, members]);
  const people = useMemo(() => {
    const ids = new Set();
    filteredEvents.forEach((event) => {
      eventMembers(event).forEach((id) => ids.add(String(id)));
    });
    return members.filter((member) => ids.has(String(member.user_id || member.id)));
  }, [filteredEvents, members]);
  const visibleEventsOn = (day) => filteredEvents.filter((event) => {
    const start = parseDate(event.start_date || event.deadline);
    const end = parseDate(event.deadline || event.start_date);
    return start && end && start <= day && end >= day;
  });
  const shiftMonth = (amount) => {
    const next = new Date(cursor.getFullYear(), cursor.getMonth() + amount, 1);
    setCursor(next);
    setStartDate(toDateValue(monthStart(next)));
    setEndDate(toDateValue(monthEnd(next)));
  };
  const apply = (event) => {
    if (event.key === 'Enter') load({ syncCursorToRange: true });
  };
  const hasActiveFilters = Boolean(searchTerm.trim()) || selectedProjectIds.length > 0 || selectedMemberIds.length > 0 || selectedStatusKeys.length > 0;
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedProjectIds([]);
    setSelectedMemberIds([]);
    setSelectedStatusKeys([]);
    setOpenFilter('');
  };

  return (
    <div className={`pm-calendar-panel ${embedded ? 'pm-calendar-panel-embedded' : ''}`}>
      {!embedded && (
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col">
              <div className="page-pretitle">Project Management</div>
              <h2 className="page-title">
                <span className="pm-calendar-title-icon"><CalendarDays size={22} /></span>
                {title}
              </h2>
              <div className="text-muted">{subtitle}</div>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary pm-refresh-button" onClick={load} disabled={loading}>
                <RefreshCw size={16} className={loading ? 'pm-spin me-2' : 'me-2'} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}

      {embedded && (
        <div className="pm-calendar-embedded-toolbar">
          <div>
            <div className="pm-calendar-embedded-title">
              <CalendarDays size={18} />
              {title}
            </div>
            <div className="text-muted pm-calendar-embedded-subtitle">{subtitle}</div>
          </div>
          <div className="btn-list">
            <button className="btn btn-primary btn-sm pm-refresh-button" onClick={load} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'pm-spin me-1' : 'me-1'} />
              Refresh
            </button>
          </div>
        </div>
      )}

      <div className={embedded ? 'pm-filter-card pm-filter-card-embedded' : 'card mb-3 pm-filter-card'}>
        <div className={embedded ? 'pm-filter-body' : 'card-body'}>
          <div className="pm-filter-heading">
            <span>Filter task allocation</span>
            <small>Use a custom range to see when members are blocked by assigned or collaborative tasks.</small>
          </div>
          <div className="row g-3 align-items-end">
            <div className="col-md-2">
              <label className="form-label">Search</label>
              <div className="pm-calendar-search-input">
                <Search size={14} />
                <input className="form-control" type="search" placeholder="Task, project, member..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={apply} />
              </div>
            </div>
            <div className="col-md-2">
              <label className="form-label">From</label>
              <input className="form-control" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} onKeyDown={apply} />
            </div>
            <div className="col-md-2">
              <label className="form-label">To</label>
              <input className="form-control" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} onKeyDown={apply} />
            </div>
            <div className="col-md-2">
              <label className="form-label">Project</label>
              <MultiSelectFilter
                filterKey="project"
                allLabel="All projects"
                options={projects.map((project) => ({ value: String(project.id), label: projectLabel(project) }))}
                selectedValues={selectedProjectIds}
                onChange={setSelectedProjectIds}
                searchPlaceholder="Search projects..."
                openFilter={openFilter}
                setOpenFilter={setOpenFilter}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Team member</label>
              <MultiSelectFilter
                filterKey="member"
                allLabel="All members"
                options={members.map((member) => ({ value: String(member.user_id || member.id), label: memberLabel(member) }))}
                selectedValues={selectedMemberIds}
                onChange={setSelectedMemberIds}
                searchPlaceholder="Search members..."
                openFilter={openFilter}
                setOpenFilter={setOpenFilter}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label">Status</label>
              <MultiSelectFilter
                filterKey="status"
                allLabel="All statuses"
                options={statuses.map((statusRow) => ({ value: String(statusRow.key_name), label: statusLabel(statusRow) }))}
                selectedValues={selectedStatusKeys}
                onChange={setSelectedStatusKeys}
                searchPlaceholder="Search statuses..."
                openFilter={openFilter}
                setOpenFilter={setOpenFilter}
              />
            </div>
            <div className="col-md-4">
              <div className="pm-calendar-filter-actions">
                <button className="btn btn-outline-primary w-100 pm-filter-button" onClick={() => load({ syncCursorToRange: true })}>Apply range</button>
                <button className="btn btn-outline-secondary w-100 pm-filter-button" onClick={clearFilters} disabled={!hasActiveFilters}>
                  <X size={14} className="me-1" />
                  Clear filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className={embedded ? 'pm-calendar-card pm-calendar-card-embedded' : 'card pm-calendar-card'}>
        <div className="card-header pm-calendar-header">
          <div className="pm-calendar-navigation">
            <button aria-label="Previous month" className="btn btn-ghost-secondary btn-sm" onClick={() => shiftMonth(-1)}><ChevronLeft size={17} /></button>
            <button aria-label="Next month" className="btn btn-ghost-secondary btn-sm" onClick={() => shiftMonth(1)}><ChevronRight size={17} /></button>
          </div>
          <h3 className="card-title mb-0">{cursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
          <span className="pm-task-count">{filteredEvents.length} blocked task{filteredEvents.length === 1 ? '' : 's'}</span>
        </div>
        <div className="pm-calendar-grid pm-calendar-weekdays">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => <div key={day}>{day}</div>)}
        </div>
        <div className="pm-calendar-grid">
          {calendarDays.map((day) => {
            const dayEvents = visibleEventsOn(day);
            const outside = day.getMonth() !== cursor.getMonth();
            const today = toDateValue(day) === toDateValue(new Date());
            return (
              <div className={`pm-calendar-day ${outside ? 'is-outside' : ''} ${today ? 'is-today' : ''}`} key={toDateValue(day)}>
                <div className="pm-calendar-day-number">{day.getDate()}{today && <span className="pm-today-label">Today</span>}</div>
                {dayEvents.map((event) => (
                  <button
                    type="button"
                    className={`pm-calendar-event ${isSubtaskEvent(event) ? 'is-subtask' : ''}`}
                    key={`${event.id}-${toDateValue(day)}`}
                    title={`${event.title} - ${projectTitle(event)}`}
                    style={eventColorStyle(event)}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <strong>{event.title}</strong>
                    <small>
                      {isSubtaskEvent(event) ? 'Subtask' : 'Task'} · {projectTitle(event)} · {eventMembers(event).map(nameFor).join(', ')}
                    </small>
                  </button>
                ))}
              </div>
            );
          })}
        </div>
        {!loading && !filteredEvents.length && <div className="p-4 text-center text-muted">No blocked task allocation matches this filter.</div>}
        {loading && <div className="p-4 text-center text-muted">Loading calendar...</div>}
      </div>

      {people.length > 0 && (
        <div className="text-muted small mt-3">
          Showing blocked allocation for: {people.map((person) => person.name || person.email).join(', ')}
        </div>
      )}

      {selectedEvent && (
        <TaskCalendarModal
          event={selectedEvent}
          assignees={eventMembers(selectedEvent).map(nameFor)}
          onClose={() => setSelectedEvent(null)}
          onOpenProject={() => {
            if (selectedEvent.project_id) navigate(`/pm/projects/${selectedEvent.project_id}`);
          }}
        />
      )}
    </div>
  );
};

const projectTitle = (event) => event.project_title || 'No project';
const projectLabel = (project) => project.title || project.project_name || `Project #${project.id}`;
const memberLabel = (member) => member.name || member.email || `User #${member.user_id || member.id}`;
const statusLabel = (status) => status.title || status.key_name || '';
const EVENT_PALETTE = [
  { accent: '#206bc4', bg: '#eff6ff', border: '#bfdbfe' },
  { accent: '#2fb344', bg: '#f0fdf4', border: '#bbf7d0' },
  { accent: '#f59f00', bg: '#fffbeb', border: '#fde68a' },
  { accent: '#ae3ec9', bg: '#faf5ff', border: '#e9d5ff' },
  { accent: '#d6336c', bg: '#fff1f2', border: '#fecdd3' },
  { accent: '#0ca678', bg: '#ecfdf5', border: '#a7f3d0' },
  { accent: '#f76707', bg: '#fff7ed', border: '#fed7aa' },
  { accent: '#7048e8', bg: '#f5f3ff', border: '#ddd6fe' },
];

const normalize = (value) => String(value || '').trim().toLowerCase();
const hashString = (value) => String(value || '').split('').reduce((hash, char) => ((hash * 31) + char.charCodeAt(0)) >>> 0, 7);
const isSubtaskEvent = (event) => Boolean(event.parent_task_id) || event.task_type === 'subtask';
const eventColorStyle = (event) => {
  const key = event.parent_task_id || event.id || event.title || event.project_id;
  const color = EVENT_PALETTE[hashString(key) % EVENT_PALETTE.length];
  return {
    '--pm-event-accent': color.accent,
    '--pm-event-bg': color.bg,
    '--pm-event-border': color.border,
  };
};
const matchesSearch = (event, searchTerm, assignees) => {
  const query = normalize(searchTerm);
  if (!query) return true;
  return [
    event.title,
    event.description,
    projectTitle(event),
    event.status_title,
    event.status,
    ...assignees,
  ].some((value) => normalize(value).includes(query));
};
const matchesSelectedFilters = (event, { selectedProjectIds, selectedMemberIds, selectedStatusKeys, assigneeIds, assigneeNames, searchTerm }) => {
  const projectMatches = selectedProjectIds.length === 0 || selectedProjectIds.includes(String(event.project_id));
  const memberMatches = selectedMemberIds.length === 0 || assigneeIds.some((id) => selectedMemberIds.includes(String(id)));
  const eventStatusKeys = [event.status, event.status_key, event.status_id].map((value) => String(value || '')).filter(Boolean);
  const statusMatches = selectedStatusKeys.length === 0 || selectedStatusKeys.some((status) => eventStatusKeys.includes(String(status)));
  return projectMatches && memberMatches && statusMatches && matchesSearch(event, searchTerm, assigneeNames);
};
const formatDate = (value) => {
  const date = parseDate(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const MultiSelectFilter = ({ filterKey, allLabel, options, selectedValues, onChange, searchPlaceholder, openFilter, setOpenFilter }) => {
  const rootRef = useRef(null);
  const [query, setQuery] = useState('');
  const open = openFilter === filterKey;
  const selectedSet = useMemo(() => new Set(selectedValues.map(String)), [selectedValues]);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalize(query);
    if (!normalizedQuery) return options;
    return options.filter((option) => normalize(option.label).includes(normalizedQuery));
  }, [options, query]);
  const selectedLabels = options.filter((option) => selectedSet.has(String(option.value))).map((option) => option.label);
  const summary = selectedLabels.length === 0
    ? allLabel
    : selectedLabels.length <= 2
      ? selectedLabels.join(', ')
      : `${selectedLabels.length} selected`;
  const toggle = (value) => {
    const key = String(value);
    onChange(selectedSet.has(key)
      ? selectedValues.filter((item) => String(item) !== key)
      : [...selectedValues, key]);
  };

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpenFilter('');
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open, setOpenFilter]);

  return (
    <div className="pm-calendar-multiselect" ref={rootRef}>
      <button type="button" className="form-select pm-calendar-multiselect-button" onClick={() => setOpenFilter(open ? '' : filterKey)} title={summary}>
        <span>{summary}</span>
      </button>
      {open && (
        <div className="pm-calendar-multiselect-menu">
          <div className="pm-calendar-multiselect-search">
            <Search size={13} />
            <input type="search" className="form-control" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={searchPlaceholder} autoFocus />
          </div>
          <button type="button" className="pm-calendar-multiselect-option" onClick={() => onChange([])}>
            <input type="checkbox" checked={selectedValues.length === 0} readOnly />
            <span>{allLabel}</span>
          </button>
          <div className="pm-calendar-multiselect-options">
            {filteredOptions.map((option) => (
              <button type="button" className="pm-calendar-multiselect-option" key={option.value} onClick={() => toggle(option.value)}>
                <input type="checkbox" checked={selectedSet.has(String(option.value))} readOnly />
                <span>{option.label}</span>
              </button>
            ))}
            {filteredOptions.length === 0 && <div className="pm-calendar-multiselect-empty">No options found</div>}
          </div>
          <div className="pm-calendar-multiselect-footer">
            <button type="button" className="btn btn-sm btn-light" onClick={() => onChange([])}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => setOpenFilter('')}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

const TaskCalendarModal = ({ event, assignees, onClose, onOpenProject }) => {
  useEffect(() => {
    const handleKeyDown = (keyEvent) => {
      if (keyEvent.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <>
      <div className="modal modal-blur fade show pm-calendar-modal" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <div className="text-muted small">Task allocation</div>
                <h5 className="modal-title">{event.title || 'Untitled task'}</h5>
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="pm-calendar-detail-grid">
                <div><span>Project</span><strong>{projectTitle(event)}</strong></div>
                <div><span>Status</span><strong>{event.status_title || event.status || '-'}</strong></div>
                <div><span>Plan Start</span><strong>{formatDate(event.start_date)}</strong></div>
                <div><span>Plan Finish</span><strong>{formatDate(event.deadline)}</strong></div>
              </div>
              <div className="mt-3">
                <div className="text-muted small fw-semibold mb-2"><Users size={14} className="me-1" />Assigned members</div>
                <div className="pm-calendar-assignee-list">
                  {assignees.length ? assignees.map((name) => <span className="badge bg-primary-lt" key={name}>{name}</span>) : <span className="text-muted small">No assignee</span>}
                </div>
              </div>
              {event.description && (
                <div className="mt-3">
                  <div className="text-muted small fw-semibold mb-1">Description</div>
                  <div className="pm-calendar-description">{event.description}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" onClick={onClose}>Close</button>
              {event.project_id && (
                <button type="button" className="btn btn-primary" onClick={onOpenProject}>
                  Open project <ArrowRight size={14} className="ms-1" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={onClose} />
    </>
  );
};

export default PmCalendarPanel;
